import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { connect } from 'react-redux';
import { injectIntl, FormattedMessage } from 'react-intl';

import { fetchVideoToken } from '../../../../actions/sessions';
import GlobalLoading from '../../../global/LoadingDots';
import PhoneCallParticipants from './PhoneCallParticipants';
import { useVonageSession } from '../../vonage/useVonageSession';
import { useVoiceCalls } from '../../vonage/useVoiceCalls';
import { useVonageDeviceSettings } from '../../vonage/device_settings/useVonageDeviceSettings';
import { resolveCaptionSpeaker } from '../../vonage/live_captions/resolveCaptionSpeaker';
import { useLiveCaptions } from '../../vonage/live_captions/useLiveCaptions';

const VonageVideo = ({
  appointment,
  sessionType,
  videoToken,
  user,
  intl,
  initialSettings,
  onDeviceSettingsReady,
  onLiveCaptionsReady,
  ...props
}) => {
  const [cameraOn, setCameraOn] = useState(
    initialSettings
      ? Boolean(
          initialSettings.cameraDeviceId && initialSettings.isCameraEnabled,
        )
      : true,
  );
  const [micOn, setMicOn] = useState(
    initialSettings ? initialSettings.isMicrophoneEnabled : true,
  );
  const { voiceCalls, onPhoneCallConnected, onPhoneCallDisconnected } =
    useVoiceCalls();
  const resolveSpeaker = useCallback(
    event =>
      resolveCaptionSpeaker({
        appointment,
        event,
        voiceCalls,
        phoneSpeakerName: call => {
          if (call.status === 'pending') {
            return intl.formatMessage({ defaultMessage: 'Phone call' });
          }
          if (call.status === 'unknown') {
            return intl.formatMessage({
              defaultMessage: 'Unknown phone caller',
            });
          }
          return intl.formatMessage(
            { defaultMessage: 'Phone call •••• {last4}' },
            { last4: call.from_number_last4 },
          );
        },
      }),
    [appointment, intl, voiceCalls],
  );
  const liveCaptions = useLiveCaptions({ appointment, resolveSpeaker });
  const onLiveCaptionsChange = useCallback(
    enabled => {
      if (enabled) liveCaptions.enableCaptions();
      else liveCaptions.disableCaptions();
    },
    [liveCaptions.enableCaptions, liveCaptions.disableCaptions],
  );
  const onJoined = useCallback(() => {
    if (initialSettings?.isLiveCaptionsEnabled) {
      liveCaptions.enableCaptions();
    }
  }, [initialSettings?.isLiveCaptionsEnabled, liveCaptions.enableCaptions]);

  useEffect(() => {
    props.fetchVideoToken(appointment.id);
  }, [appointment.id, props.fetchVideoToken]);

  const getDivId = userId => {
    if (!appointment?.id) return 'remote-media-div';
    if (appointment.provider_type === 'couples') {
      if (
        String(userId) ===
        String(appointment.couples_appointment_detail?.guest?.id)
      ) {
        return 'remote-guest-media';
      }
      return 'remote-host-media';
    }

    return 'remote-media-div';
  };

  const {
    error,
    participantPresent: clientPresent,
    partnerPresent,
    setCameraEnabled,
    setMicEnabled,
    publisher,
    hasVideoSource,
  } = useVonageSession({
    appointment,
    user,
    token: videoToken?.token,
    role: 'provider',
    getDivId,
    couplesPartnerDivId: 'remote-guest-media',
    phoneCallMediaDivId: 'phone-call-media',
    onPhoneCallConnected,
    onPhoneCallDisconnected,
    initialSettings,
    onJoined,
    captionsEnabled: liveCaptions.isEnabled,
    onCaptionReceived: liveCaptions.onCaptionReceived,
    onCaptionStreamDestroyed: liveCaptions.onCaptionStreamDestroyed,
    onCaptionsInterrupted: liveCaptions.clearPartialCaptions,
  });

  const deviceSettings = useVonageDeviceSettings({
    publisher,
    hasVideoSource,
    isMicrophoneEnabled: micOn,
    isCameraEnabled: cameraOn,
    initialSettings,
  });

  useEffect(() => {
    onDeviceSettingsReady({ controller: deviceSettings, hasVideoSource });
  }, [
    hasVideoSource,
    deviceSettings.selectedMicrophoneId,
    deviceSettings.selectedCameraId,
    deviceSettings.selectedSpeakerId,
    deviceSettings.changing,
    deviceSettings.errors,
    deviceSettings.micLevel,
    deviceSettings.playing,
    onDeviceSettingsReady,
  ]);

  useEffect(() => {
    onLiveCaptionsReady({
      available: liveCaptions.available,
      isEnabled: liveCaptions.isEnabled,
      isStarting: liveCaptions.isStarting,
      onChange: onLiveCaptionsChange,
      captionState: liveCaptions.captionState,
      languageCode: liveCaptions.languageCode,
      onClose: liveCaptions.disableCaptions,
    });
  }, [
    liveCaptions.available,
    liveCaptions.captionState,
    liveCaptions.disableCaptions,
    liveCaptions.isEnabled,
    liveCaptions.isStarting,
    liveCaptions.languageCode,
    onLiveCaptionsChange,
    onLiveCaptionsReady,
  ]);

  useEffect(() => {
    if (props.callbackForControls) {
      props.callbackForControls({
        toggleCamera: () => {
          if (!hasVideoSource) return false;
          const newState = !cameraOn;
          setCameraOn(newState);
          setCameraEnabled(newState);
          return true;
        },
        toggleMic: () => {
          const newState = !micOn;
          setMicOn(newState);
          setMicEnabled(newState);
        },
      });
    }
  }, [
    props.callbackForControls,
    cameraOn,
    micOn,
    setCameraEnabled,
    setMicEnabled,
    hasVideoSource,
  ]);

  const renderLoadingMessage = clientArrived => {
    const clientWillArriveSoon = (
      <FormattedMessage
        defaultMessage="The person you are waiting for will arrive soon. If you don't see them and it is after the scheduled appointment start time, message them directly in the chat box. For more information, <a>visit the Help Centre</a>"
        values={{
          a: chunks => (
            <a
              href={
                intl.locale === 'en'
                  ? 'https://inkblot.zendesk.com/hc/en-us/articles/360053062011-Troubleshooting-Cannot-connect-to-my-appointment'
                  : 'https://inkblot.zendesk.com/hc/fr-ca/articles/360053062011-Troubleshooting-Cannot-connect-to-my-appointment'
              }
            >
              {chunks}
            </a>
          ),
        }}
      />
    );

    return clientArrived ? null : clientWillArriveSoon;
  };

  const renderCouplesRemotes = () => (
    <div
      id="remote-couples"
      className="remote-couples"
      style={{ display: sessionType === 'couples' ? 'flex' : 'none' }}
    >
      <div
        id="remote-host-media"
        className="remote-couples-media"
        style={{
          borderRadius: 8,
          width: partnerPresent ? 'calc(50% - 6px)' : '100%',
        }}
      >
        <GlobalLoading
          style={{ paddingBottom: '0px' }}
          message={renderLoadingMessage(clientPresent)}
        />
      </div>
      <div
        id="remote-guest-media"
        className="remote-couples-media"
        style={{ borderRadius: 8, display: partnerPresent ? 'flex' : 'none' }}
      >
        <GlobalLoading
          style={{ paddingBottom: '0px' }}
          message={renderLoadingMessage(partnerPresent)}
        />
      </div>
    </div>
  );

  return (
    <div className="video-screen-wrapper">
      {error && (
        <div className="connection-error-message">
          <FormattedMessage defaultMessage="We were unable to connect you to the session. Please refresh the page to try again." />
        </div>
      )}
      {voiceCalls.length > 0 && (
        <PhoneCallParticipants voiceCalls={voiceCalls} />
      )}
      <div id="remote-media-div">
        {sessionType === 'couples' && renderCouplesRemotes()}
      </div>
      <div id="local-media" style={{ borderRadius: 8 }} />
      <div id="phone-call-media" style={{ display: 'none' }} />
      {sessionType !== 'couples' && (
        <GlobalLoading
          style={{ paddingBottom: '0px' }}
          message={renderLoadingMessage(clientPresent)}
        />
      )}
    </div>
  );
};

VonageVideo.propTypes = {
  appointment: PropTypes.object.isRequired,
  sessionType: PropTypes.string.isRequired,
  videoToken: PropTypes.object,
  user: PropTypes.object.isRequired,
  intl: PropTypes.object.isRequired,
  fetchVideoToken: PropTypes.func.isRequired,
  callbackForControls: PropTypes.func.isRequired,
  initialSettings: PropTypes.object,
  onDeviceSettingsReady: PropTypes.func,
  onLiveCaptionsReady: PropTypes.func,
};

VonageVideo.defaultProps = {
  initialSettings: null,
  onDeviceSettingsReady: () => {},
  onLiveCaptionsReady: () => {},
};

function mapStateToProps(state) {
  const videoToken = _.get(state, 'session.videoToken');

  return {
    videoToken,
  };
}

export default injectIntl(
  connect(mapStateToProps, {
    fetchVideoToken,
  })(VonageVideo),
);
